// Date utility functions for period calculations

/**
 * Get period bounds for filtering
 * @param {string} period - 'week', 'month', 'year', 'all'
 * @returns {Object} {start, end} dates
 */
export const getPeriodBounds = (period) => {
  const today = new Date();
  const start = new Date();
  let end = new Date();
  
  switch (period) {
    case 'week':
      // Start of current week (Monday)
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      break;
      
    case 'month':
      // Start of current month
      start.setDate(1);
      // End of current month
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      break;
      
    case 'year':
      // Start of current year
      start.setMonth(0, 1);
      // End of current year
      end.setMonth(11, 31);
      break;
      
    case 'all':
      // For "all time", we'll use a default range
      // The actual start will come from the API
      start.setFullYear(start.getFullYear() - 1); // Last year as default
      break;
      
    default:
      // Last 30 days
      start.setDate(start.getDate() - 30);
  }
  
  // Reset time to start/end of day
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  
  return { start, end };
};

/**
 * Format date to YYYY-MM-DD for API
 * @param {Date} date 
 * @returns {string}
 */
export const formatDateISO = (date) => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

/**
 * Get readable month names
 * @param {number} monthIndex - 0-11
 * @returns {string}
 */
export const getMonthName = (monthIndex) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[monthIndex] || '';
};

/**
 * Get month options for dropdown (last 12 months)
 * @param {Date} firstLogDate - First log date from user
 * @returns {Array} Array of {value: 'YYYY-MM', label: 'Month Year'}
 */
export const getMonthOptions = (firstLogDate) => {
  const options = [];
  const today = new Date();
  let startDate = new Date(today.getFullYear(), today.getMonth() - 11, 1); // Last 12 months
  
  // If user has older data, start from their first log
  if (firstLogDate && firstLogDate < startDate) {
    startDate = new Date(firstLogDate.getFullYear(), firstLogDate.getMonth(), 1);
  }
  
  const current = new Date(startDate);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();
    options.push({
      value: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: `${getMonthName(month)} ${year}`
    });
    current.setMonth(current.getMonth() + 1);
  }
  
  return options;
};

/**
 * Get year options for dropdown
 * @param {Date} firstLogDate - First log date from user
 * @returns {Array} Array of {value: 'YYYY', label: 'Year YYYY'}
 */
export const getYearOptions = (firstLogDate) => {
  const currentYear = new Date().getFullYear();
  let startYear = currentYear;
  
  if (firstLogDate) {
    startYear = Math.min(startYear, firstLogDate.getFullYear());
  }
  
  const options = [];
  for (let year = startYear; year <= currentYear; year++) {
    options.push({
      value: year.toString(),
      label: `Year ${year}`
    });
  }
  
  return options;
};

/**
 * Format date for display
 * @param {Date|string} date 
 * @param {boolean} includeYear 
 * @returns {string}
 */
export const formatDateDisplay = (date, includeYear = true) => {
  if (!date) return 'N/A';
  
  const d = new Date(date);
  const options = { 
    month: 'short', 
    day: 'numeric',
    ...(includeYear && { year: 'numeric' })
  };
  
  return d.toLocaleDateString('en-US', options);
};